<template>
  <div
    class="subtitle-line"
    :class="{
      active: isActive,
      editing: isEditing,
      'subtitle-hidden': !isSubtitlesVisible,
    }"
    :style="{ '--line-opacity': lineOpacity }"
    @click="handleLineClick"
    @dblclick.stop="handleLineDoubleClick"
  >
    <template v-if="isEditing">
      <textarea
        :value="editingText"
        :style="{ fontSize: fontSize + 'px' }"
        @keydown.enter.prevent="$emit('finish-edit')"
        @keydown.esc.prevent="$emit('cancel-edit')"
        @input="$emit('update:editingText', $event.target.value)"
      ></textarea>
      <div class="edit-actions">
        <button class="btn-action" @click.stop="$emit('finish-edit')" :title="$t('common.save')">
          <span class="line-icon icon-check"></span>
        </button>
        <button class="btn-action" @click.stop="$emit('cancel-edit')" :title="$t('common.cancel')">
          <span class="line-icon icon-close"></span>
        </button>
      </div>
    </template>

    <template v-else>
      <div class="subtitle-text" :style="{ fontSize: fontSize + 'px' }">
        <div v-if="isActive && isFollowingMode" class="progress-overlay" :style="{ width: followingProgress + '%' }"></div>
        <span v-if="showTimestamps" class="timestamp">{{ formattedStart }}</span>
        <p class="text-content">
          <span class="text-body" v-html="displayText"></span>
          <!-- 操作靠右与句子最后一行齐平：评分和回放常显并贴最右；解释和编辑排在左边，只在当前句或鼠标所在句出现 -->
          <span class="line-actions">
            <button
              v-if="showExplanationAction"
              class="btn-action action-reveal action-explain"
              :class="{ active: isExplanationOpen }"
              type="button"
              :title="$t('sentenceExplanation.action')"
              :aria-label="$t('sentenceExplanation.action')"
              :aria-expanded="isExplanationOpen"
              @click.stop="$emit('toggle-explanation')"
            >
              <span class="line-icon icon-sparkles"></span>
            </button>
            <button
              v-if="showEditButtons"
              class="btn-action action-reveal action-edit"
              @click.stop="$emit('start-edit')"
              title="Edit subtitle"
              aria-label="Edit subtitle"
            >
              <span class="line-icon icon-edit"></span>
            </button>
            <button
              v-if="assessment"
              class="assessment-badge"
              :class="[assessment.status, assessmentLevel]"
              type="button"
              :title="assessmentTitle"
              :aria-expanded="isAssessmentOpen"
              @click.stop="toggleAssessmentDetails"
            >
              {{ assessmentLabel }}
            </button>
            <button
              v-if="showRecordingActions && hasRecording"
              class="btn-action"
              :class="{ active: isPlayingRecording }"
              @click.stop="$emit('play-recording')"
              :title="$t('audioPlayer.playOriginalRecording')"
              :aria-label="$t('audioPlayer.playOriginalRecording')"
            >
              <span :class="['line-icon', isPlayingRecording ? 'icon-pause' : 'icon-play']"></span>
            </button>
          </span>
        </p>
        <p v-if="translatedText" class="subtitle-translation">{{ translatedText }}</p>
      </div>
    </template>
  </div>
</template>

<script>
// 非当前句的透明度：暂停时统一 0.62 保证全文可读；播放时离当前句越远越淡
const RESTING_OPACITY = 0.72
const OPACITY_STEP = 0.1
const MIN_OPACITY = 0.32

export default {
  name: 'SubtitleItem',
  emits: [
    'play',
    'play-recording',
    'toggle-assessment-details',
    'toggle-explanation',
    'word-click',
    'start-edit',
    'finish-edit',
    'cancel-edit',
    'update:editingText',
  ],
  props: {
    formattedStart: { type: String, required: true },
    displayText: { type: String, required: true },
    translatedText: { type: String, default: '' },
    isActive: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    isSubtitlesVisible: { type: Boolean, default: true },
    isFollowingMode: { type: Boolean, default: false },
    followingProgress: { type: Number, default: 0 },
    // 播放时与当前句相隔的行数；0 表示不做远近淡化
    focusDistance: { type: Number, default: 0 },
    showTimestamps: { type: Boolean, default: true },
    showEditButtons: { type: Boolean, default: true },
    showRecordingActions: { type: Boolean, default: false },
    showExplanationAction: { type: Boolean, default: true },
    isExplanationOpen: { type: Boolean, default: false },
    hasRecording: { type: Boolean, default: false },
    isPlayingRecording: { type: Boolean, default: false },
    assessment: { type: Object, default: null },
    isAssessmentOpen: { type: Boolean, default: false },
    fontSize: { type: Number, default: 24 },
    editingText: { type: String, default: '' },
  },
  computed: {
    lineOpacity() {
      if (this.isActive || this.isEditing) return 1
      const distance = Math.max(1, this.focusDistance)
      return Math.max(MIN_OPACITY, RESTING_OPACITY - distance * OPACITY_STEP)
    },
    assessmentLabel() {
      if (!this.assessment) return ''
      if (this.assessment.status === 'assessing') return '...'
      if (this.assessment.status === 'error') return '!'
      return this.assessment.pronunciationScore ?? '-'
    },
    assessmentTitle() {
      if (!this.assessment) return ''
      if (this.assessment.status === 'assessing') return this.assessmentText('assessing')
      if (this.assessment.status === 'error') return this.assessment.error || this.assessmentText('assessmentFailed')
      return [
        `${this.assessmentText('pronunciation')}: ${this.assessment.pronunciationScore ?? '-'}`,
        `${this.assessmentText('accuracy')}: ${this.assessment.accuracyScore ?? '-'}`,
        `${this.assessmentText('fluency')}: ${this.assessment.fluencyScore ?? '-'}`,
        `${this.assessmentText('completeness')}: ${this.assessment.completenessScore ?? '-'}`,
        `${this.assessmentText('assessmentLanguage')}: ${this.assessment.language || '-'}`,
      ].join('\n')
    },
    assessmentLevel() {
      if (!this.assessment || this.assessment.status !== 'done') return ''
      const score = this.assessment.pronunciationScore
      if (score === null || score === undefined) return 'unknown'
      if (score >= 85) return 'excellent'
      if (score >= 70) return 'good'
      if (score >= 50) return 'needs-practice'
      return 'poor'
    },
  },
  methods: {
    // 只有真正的按钮和输入框不触发跳转
    isInteractiveTarget(target) {
      return Boolean(target?.closest?.('button, textarea, input'))
    },
    handleLineClick(event) {
      if (this.isEditing) return
      const wordTarget = event.target?.closest?.('.lookup-word')
      if (wordTarget) {
        const rect = wordTarget.getBoundingClientRect()
        this.$emit('word-click', {
          word: wordTarget.dataset.word || wordTarget.textContent || '',
          start: Number(wordTarget.dataset.start),
          position: {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          },
        })
        return
      }
      if (this.isInteractiveTarget(event.target)) return
      this.$emit('play')
    },
    handleLineDoubleClick(event) {
      if (this.isEditing || this.isInteractiveTarget(event.target)) return
      this.$emit('start-edit')
    },
    assessmentText(key) {
      return this.$t(`audioPlayer.pronunciationAssessment.${key}`)
    },
    toggleAssessmentDetails() {
      if (this.assessment?.status === 'done') {
        this.$emit('toggle-assessment-details')
      }
    },
  },
}
</script>

<style scoped>
/* 字幕行：没有分隔线和底色，靠大号字、明暗和留白区分当前句 */
.subtitle-line {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  box-sizing: border-box;
  border-radius: var(--radius-md);
  cursor: pointer;
  opacity: var(--line-opacity, 1);
  transition: opacity 0.35s ease, background-color 0.2s ease;
}

.subtitle-line:not(.active):hover {
  opacity: 1;
  background: rgba(16, 24, 32, 0.035);
}

.subtitle-text {
  position: relative;
  min-width: 0;
}

.text-content,
.subtitle-translation {
  position: relative;
  z-index: 1;
  margin: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  user-select: none;
}

.text-body,
.subtitle-translation {
  white-space: pre-wrap;
  transition: filter 0.3s ease;
}

.text-content {
  color: var(--ink-primary);
  font-weight: 600;
  line-height: 1.38;
  letter-spacing: -0.012em;
}

.subtitle-translation {
  margin-top: 0.35em;
  color: var(--ink-secondary);
  font-size: 0.78em;
  font-weight: 400;
  line-height: 1.5;
}

/* 当前句逐词推进：读过的实色、当前词薄荷色、还没读到的半透明 */
:deep(.word-highlight) {
  color: var(--signal-active-strong);
}

:deep(.word-upcoming) {
  color: color-mix(in srgb, var(--ink-primary) 38%, transparent);
}

:deep(.lookup-word) {
  border-radius: var(--radius-sm);
}

:deep(.lookup-word:hover) {
  background: var(--surface-active);
}

:deep(.saved-word) {
  background: linear-gradient(
    transparent 62%,
    color-mix(in srgb, var(--signal-marker) 55%, transparent) 62%
  );
}

:deep(.assessment-word-Mispronunciation),
:deep(.assessment-word-poor) {
  text-decoration: underline;
  text-decoration-color: var(--color-danger);
  text-decoration-thickness: 0.12em;
  text-underline-offset: 0.2em;
}

:deep(.assessment-word-needs-practice) {
  text-decoration: underline;
  text-decoration-color: var(--color-warning);
  text-decoration-thickness: 0.1em;
  text-underline-offset: 0.2em;
}

:deep(.assessment-word-Omission) {
  border: 1px dashed var(--color-danger);
  border-radius: var(--radius-sm);
  padding: 0 0.12em;
}

:deep(.assessment-word-Insertion) {
  background: var(--color-danger-bg);
  border-radius: var(--radius-sm);
  padding: 0 0.12em;
}

:deep(.assessment-word-UnexpectedBreak),
:deep(.assessment-word-MissingBreak),
:deep(.assessment-word-Monotone) {
  text-decoration: underline dotted #7c3aed;
  text-decoration-thickness: 0.1em;
  text-underline-offset: 0.2em;
}

/* 跟读等待时当前句背后的进度填充 */
.progress-overlay {
  position: absolute;
  top: -0.2em;
  bottom: -0.2em;
  left: -0.4em;
  z-index: 0;
  max-width: calc(100% + 0.8em);
  border-radius: var(--radius-sm);
  background: var(--surface-active);
  pointer-events: none;
  transition: width 0.08s linear;
}

/* 隐藏字幕：强高斯模糊，只看得出句子长短，读不出内容；句末操作保持清晰可点 */
.subtitle-hidden .text-body,
.subtitle-hidden .subtitle-translation {
  filter: blur(0.5em);
  pointer-events: none;
}

.timestamp {
  display: block;
  margin-bottom: 0.2em;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-feature-settings: "tnum" 1;
  user-select: none;
}

/* 句末操作：一组小图标靠右，和句子最后一行齐平；最后一行放不下时整组落到下一行右侧 */
.text-content {
  display: flow-root;
}

/* 没有可见按钮时整组宽度为 0，不会单独占一行；间距由每个可见按钮自己带 */
.line-actions {
  float: right;
  display: flex;
  align-items: center;
  height: 1.38em;
  white-space: nowrap;
}

.line-actions > * {
  margin-left: 6px;
}

.line-actions .action-reveal {
  display: none;
}

.subtitle-line.active .action-explain,
.line-actions .action-reveal.active {
  display: inline-flex;
}

@media (hover: hover) {
  .subtitle-line:hover .action-reveal {
    display: inline-flex;
  }
}

.edit-actions {
  display: flex;
  gap: var(--space-1);
}

.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  color: var(--ink-tertiary);
  background: transparent;
  border: 0;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background-color 0.16s ease, color 0.16s ease;
}

.subtitle-line.active .btn-action {
  color: var(--ink-secondary);
}

.subtitle-line .btn-action:hover,
.subtitle-line .btn-action.active {
  color: var(--ink-primary);
  background: rgba(16, 24, 32, 0.07);
}

.btn-action:focus-visible,
.assessment-badge:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand);
}

.line-icon {
  display: block;
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

.icon-play { mask-image: url('~@/assets/ui-icons/play.svg'); -webkit-mask-image: url('~@/assets/ui-icons/play.svg'); }
.icon-pause { mask-image: url('~@/assets/ui-icons/pause.svg'); -webkit-mask-image: url('~@/assets/ui-icons/pause.svg'); }
.icon-edit { mask-image: url('~@/assets/ui-icons/edit.svg'); -webkit-mask-image: url('~@/assets/ui-icons/edit.svg'); }
.icon-check { mask-image: url('~@/assets/ui-icons/check.svg'); -webkit-mask-image: url('~@/assets/ui-icons/check.svg'); }
.icon-close { mask-image: url('~@/assets/ui-icons/close.svg'); -webkit-mask-image: url('~@/assets/ui-icons/close.svg'); }
.icon-sparkles { mask-image: url('~@/assets/ui-icons/sparkles.svg'); -webkit-mask-image: url('~@/assets/ui-icons/sparkles.svg'); }

.assessment-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 22px;
  padding: 0 6px;
  color: var(--ink-secondary);
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-weight: 600;
  cursor: pointer;
}

.assessment-badge.excellent {
  color: #166534;
  background: var(--color-success-bg);
  border-color: rgba(22, 101, 52, 0.2);
}

.assessment-badge.good {
  color: var(--signal-active-strong);
  background: var(--surface-active);
  border-color: var(--border-active);
}

.assessment-badge.needs-practice {
  color: #92400e;
  background: var(--color-warning-bg);
  border-color: rgba(217, 119, 6, 0.24);
}

.assessment-badge.poor,
.assessment-badge.error {
  color: #b91c1c;
  background: var(--color-danger-bg);
  border-color: rgba(225, 29, 72, 0.22);
}

.assessment-badge[aria-expanded='true'] {
  border-color: currentColor;
}

.subtitle-line textarea {
  width: 100%;
  min-height: 4.25rem;
  padding: 0.55rem 0.65rem;
  box-sizing: border-box;
  color: var(--ink-primary);
  background: var(--surface-panel);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  font-family: inherit;
  font-weight: 600;
  line-height: 1.38;
  resize: none;
  outline: none;
}

.subtitle-line textarea:focus {
  border-color: var(--border-active);
  box-shadow: var(--focus-ring-brand);
}

@media (max-width: 768px) {
  .subtitle-line:not(.active):hover {
    background: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .subtitle-line,
  .subtitle-text {
    transition: none;
  }
}
</style>
